/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import NavigationLayout from '../../../components/NavigationLayout';
import Overview from './Overview';
import Plans from './Plans';
import History from './History';
import Address from './Address';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { UserContext } from '../../../contexts/User';

const TABS = ['Overview', 'Plans', 'History', 'Address'];

function Payments() {
  const location = useLocation();
  const history = useHistory();
  const { activeWorkspace, refreshWorkspaceToken } = useContext(UserContext);

  // Refresh page data after a subscription/renewal succeeds: pull the latest
  // workspace (membership + plan expiry) so the header subscription banner,
  // Overview tab and Plans buttons all reflect the new subscription, and drop
  // the renew flag so the Plans tab leaves renewal mode.
  const handleSubscriptionSuccess = async () => {
    try {
      await refreshWorkspaceToken();
    } catch (error) {
      console.error('Failed to refresh workspace after subscription:', error);
    }
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('renew')) {
      searchParams.delete('renew');
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
        state: location.state,
      });
    }
  };

  const getTabFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const tabIndex = tabParam !== null ? parseInt(tabParam, 10) : null;
    // eslint-disable-next-line no-restricted-globals
    if (tabIndex !== null && !isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) return tabIndex;
    return null;
  };

  const { selectedTab } = location.state || {};

  const [value, setValue] = useState(() => {
    const queryTab = getTabFromQuery();
    return queryTab !== null ? queryTab : (selectedTab || 0);
  });

  useEffect(() => {
    const queryTab = getTabFromQuery();
    if (queryTab !== null && queryTab !== value) setValue(queryTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Handle Stripe redirect after payment
  useEffect(() => {
    const handleStripeRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentIntent = urlParams.get('payment_intent');
      const redirectStatus = urlParams.get('redirect_status');
      const transactionId = sessionStorage.getItem('pendingTransactionId');

      if (paymentIntent && redirectStatus && transactionId && activeWorkspace) {
        history.replace(location.pathname);
        const status = redirectStatus === 'succeeded' ? 'success' : 'failed';
        try {
          await sendRequest({
            method: 'put',
            url: API.WORKSPACE.updateTransactionStatus(activeWorkspace.id, transactionId),
            data: { status, paymentIntentId: paymentIntent },
          });
          sessionStorage.removeItem('pendingTransactionId');

          // Refresh workspace data so the header banner / Overview reflect the
          // new subscription once a redirect-based (e.g. 3DS) payment succeeds.
          if (status === 'success') {
            try {
              await refreshWorkspaceToken();
            } catch (refreshError) {
              console.error('Failed to refresh workspace after redirect:', refreshError);
            }
          }

          // Switch to history tab to show the updated transaction
          setValue(2);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update transaction status after redirect:', error);
        }
      }
    };
    handleStripeRedirect();
  }, [location, history, activeWorkspace, refreshWorkspaceToken]);

  const handleChange = (newValue) => {
    setValue(newValue);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', newValue);
    history.replace({ pathname: location.pathname, search: searchParams.toString(), state: location.state });
  };

  const renderPanel = () => {
    switch (value) {
      case 0: return <Overview setTab={setValue} />;
      case 1: return <Plans onSubscriptionSuccess={handleSubscriptionSuccess} />;
      case 2: return <History />;
      case 3: return <Address />;
      default: return null;
    }
  };

  return (
    <div>
      <NavigationLayout
        title="Payment & Billing"
        compact
        onBack={() => history.goBack()}
      >
        <div className="bg-white border border-rule rounded-[12px] overflow-hidden" style={{ borderStyle: 'solid', boxShadow: '0 1px 2px rgba(14, 30, 64, 0.04)', marginTop: 16, fontFamily: 'Inter, sans-serif' }}>
          <div className="flex items-center gap-1 px-3 overflow-x-auto border-b border-rule" style={{ borderBottomStyle: 'solid' }}>
            {TABS.map((label, i) => {
              const active = value === i;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleChange(i)}
                  className={`relative inline-flex items-center px-4 py-3 text-sm bg-transparent border-0 cursor-pointer whitespace-nowrap transition ${active ? 'text-ink-900 font-semibold' : 'text-ink-500 font-medium hover:text-ink-900'}`}
                >
                  {label}
                  {active && <span className="absolute left-2 right-2 bottom-0" style={{ height: 2, background: '#213d69', borderRadius: 2 }} />}
                </button>
              );
            })}
          </div>
          {renderPanel()}
        </div>
      </NavigationLayout>
    </div>
  );
}

export default Payments;
